import type { IUser } from '@manager/api/types/auth-types';
import { buildModalRoute } from '@manager/modules/modals/routable/modal-routable-utils';
import { buildMoveOutModalRoute } from '@manager/modules/move-out/move-out-routes';
import { routeNames } from '@manager/router/route-names';
import type { IPermission } from '@manager/types/permission-types';
import type { RouteMetaConfig } from '@manager/types/router';
import type { RouteLocationNamedRaw } from 'vue-router';

export const bookingRoutes: RouteMetaConfig[] = [
  {
    path: '/bookings',
    name: routeNames.bookingOverview,
    component: () =>
      import('@manager/modules/booking/views/BookingListView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'bookings.view',
      listFilterName: 'bookings',
    },
    children: [
      buildModalRoute(
        {
          name: routeNames.bookingCreate,
          path: 'create',
          component: () =>
            import('@manager/modules/booking/views/BookingCreateForm.vue'),
        },
        { fullscreen: true }
      ),
      buildModalRoute(
        {
          path: 'export',
          name: routeNames.bookingExport,
          meta: {
            requiresAuth: true,
            permission: 'bookings.export',
          },
          component: () => import('./views/BookingExportView.vue'),
        },
        { fullscreen: false, width: 'unset' }
      ),
    ],
  },
  {
    path: '/bookings/:bookingId(\\d+)',
    name: routeNames.bookingDetail,
    component: () =>
      import('@manager/modules/booking/views/BookingDetailView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'bookings.view',
    },
    children: [
      buildModalRoute(
        {
          name: routeNames.bookingAdjust,
          path: 'adjust',
          meta: {
            requiresAuth: true,
            permission: ['booking_adjustments.view'] as IPermission[],
          },
          component: () =>
            import('@manager/modules/adjust-booking/views/BookingAdjustView.vue'),
        },
        { fullscreen: true }
      ),
      buildMoveOutModalRoute(routeNames.bookingMoveOut, 'move-out'),
      buildModalRoute(
        {
          path: 'edit/notes',
          name: routeNames.bookingEditNotes,
          meta: {
            requiresAuth: true,
            permission: 'bookings.update',
          },
          component: () =>
            import('@manager/modules/booking/views/BookingEditNoteForm.vue'),
        },
        { fullscreen: false, width: 'md' }
      ),
      buildModalRoute(
        {
          path: 'edit/access-management',
          name: routeNames.bookingEditAccessManagement,
          meta: {
            requiresAuth: true,
            permission: 'booking_access_permissions.update',
          },
          component: () =>
            import('@manager/modules/booking/views/BookingEditAccessManagement.vue'),
        },
        { fullscreen: false, width: 'md' }
      ),
      buildModalRoute(
        {
          path: 'edit/custom-invoice-fields',
          name: routeNames.bookingEditCustomInvoiceFields,
          meta: {
            requiresAuth: true,
            permission: 'bookings.update',
          },
          component: () =>
            import('./views/BookingEditCustomInvoiceFieldsView.vue'),
        },
        { fullscreen: false, width: 'md' }
      ),
      buildModalRoute(
        {
          path: 'edit/customer-shared-access/:sharedAccessId',
          name: routeNames.bookingEditCustomerSharedAccess,
          meta: {
            requiresAuth: true,
            permission: 'customer_shared_access_permissions.update',
          },
          component: () =>
            import('@manager/modules/booking/views/BookingEditCustomerSharedAccessView.vue'),
        },
        { fullscreen: false, width: 'md' }
      ),
      buildModalRoute(
        {
          path: 'upcoming-invoice',
          name: routeNames.bookingUpcomingInvoice,
          meta: {
            requiresAuth: true,
            permission: 'bookings.view',
            additionalCheck: (user: IUser) =>
              user.tenant_features.uses_kinnovis_billing,
          },
          component: () =>
            import('@manager/modules/booking/views/BookingUpcomingInvoicePreviewView.vue'),
        },
        { fullscreen: false, width: 'xl' }
      ),
    ],
  },
];

export const bookingRoutesFactory = {
  bookingOverview() {
    return {
      name: routeNames.bookingOverview,
    };
  },
  bookingCreate(options?: { customerId?: string | number }) {
    return {
      name: routeNames.bookingCreate,
      query: {
        customerId: options?.customerId,
      },
    };
  },
  bookingDetail(bookingId: string | number): RouteLocationNamedRaw {
    return {
      name: routeNames.bookingDetail,
      params: {
        bookingId,
      },
    };
  },
  bookingEditNotes(bookingId: string | number): RouteLocationNamedRaw {
    return {
      name: routeNames.bookingEditNotes,
      params: {
        bookingId,
      },
    };
  },
  bookingEditAccessManagement(
    bookingId: string | number
  ): RouteLocationNamedRaw {
    return {
      name: routeNames.bookingEditAccessManagement,
      params: {
        bookingId,
      },
    };
  },
  bookingEditCustomerSharedAccess(
    sharedAccessId: string | number
  ): RouteLocationNamedRaw {
    return {
      name: routeNames.bookingEditCustomerSharedAccess,
      params: {
        sharedAccessId,
      },
    };
  },
  bookingEditCustomInvoiceFields(
    bookingId: string | number
  ): RouteLocationNamedRaw {
    return {
      name: routeNames.bookingEditCustomInvoiceFields,
      params: {
        bookingId,
      },
    };
  },
  bookingAdjust(bookingId: string | number): RouteLocationNamedRaw {
    return {
      name: routeNames.bookingAdjust,
      params: {
        bookingId,
      },
    };
  },
  bookingMoveOut(bookingId: string | number): RouteLocationNamedRaw {
    return {
      name: routeNames.bookingMoveOut,
      params: {
        bookingId,
      },
    };
  },
  bookingUpcomingInvoice(bookingId: string | number): RouteLocationNamedRaw {
    return {
      name: routeNames.bookingUpcomingInvoice,
      params: {
        bookingId,
      },
    };
  },
};
